import axios from 'axios';
import { showGlobalNotification } from '../contexts/NotificationContext';
import { globalNavigate } from './navigation';

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

const triggerForceLogout = (displayMsg) => {
  if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/auth')) {
    window.localStorage.clear();
    if (displayMsg) {
      window.localStorage.setItem('logoutNotification', displayMsg);
    }
    // Use hard redirect to clear React Router state and reset JWTContext isLoggedIn state,
    // avoiding infinite redirect loop / flickering between GuestGuard and AuthGuard.
    window.location.replace('/login');
  }
};

instance.interceptors.request.use(
  (request) => {
    const accessToken = window.localStorage.getItem('accessToken');
    request.headers = request.headers || {};
    request.headers.WebAdmin = true;
    if (accessToken) {
      request.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (!request.headers['Content-Type']) {
      request.headers['Content-Type'] = 'application/json';
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    if (response?.data?.success === false) {
      const serverMessage = response.data?.message || response.data?.error || '';
      const msgStr = serverMessage.toUpperCase();
      if (
        msgStr.includes('FORCE_LOGOUT') ||
        msgStr.includes('ACCOUNT_DEACTIVATED') ||
        msgStr === 'INACTIVE' ||
        msgStr === 'DEACTIVATED' ||
        (msgStr.includes('ACCOUNT') && (msgStr.includes('INACTIVE') || msgStr.includes('DEACTIVATED'))) ||
        (msgStr.includes('USER') && (msgStr.includes('INACTIVE') || msgStr.includes('DEACTIVATED'))) ||
        msgStr.includes('SESSION EXPIRED') ||
        msgStr.includes('INVALID TOKEN') ||
        msgStr.includes('LOG IN AGAIN')
      ) {
        let displayMsg = 'Your session has expired. Please sign in again.';
        if (
          msgStr.includes('ACCOUNT_DEACTIVATED') ||
          msgStr === 'INACTIVE' ||
          msgStr === 'DEACTIVATED' ||
          (msgStr.includes('ACCOUNT') && (msgStr.includes('INACTIVE') || msgStr.includes('DEACTIVATED'))) ||
          (msgStr.includes('USER') && (msgStr.includes('INACTIVE') || msgStr.includes('DEACTIVATED')))
        ) {
          displayMsg = 'Your account is inactive. Please contact your Admin.';
        }
        triggerForceLogout(displayMsg);
        return Promise.reject({
          message: serverMessage,
          status: response.status,
          data: response.data
        });
      }
      if (msgStr.includes('PERMISSIONS HAVE BEEN UPDATED') || msgStr.includes('SYNC YOUR NEW PERMISSIONS')) {
        triggerForceLogout(
          serverMessage || 'Your permissions have been updated by your administrator. Please log in again to sync your new permissions.'
        );
        return Promise.reject({
          message: serverMessage,
          status: response.status,
          data: response.data
        });
      }
    }
    if (response?.data?.success === true) {
      const respData = response.data.data;
      const subInfo = response.data.subscription;

      let hasSubscription = undefined;
      let subscriptionTier = undefined;

      if (subInfo && typeof subInfo === 'object' && subInfo.isSubscribe !== undefined) {
        hasSubscription = subInfo.isSubscribe;
        subscriptionTier = subInfo.tier;
      } else if (respData && respData.has_subscription !== undefined) {
        hasSubscription = respData.has_subscription;
        subscriptionTier = respData.plan?.code || respData.plan_code || respData.subscription;
      }

      if (hasSubscription !== undefined) {
        let isSuperAdmin = false;
        const userInfoStr = localStorage.getItem('userInfo');
        if (userInfoStr) {
          try {
            const userInfo = JSON.parse(userInfoStr);
            isSuperAdmin = userInfo.role_name === 'SuperAdmin';
            if (hasSubscription === false) {
              userInfo.subscription = isSuperAdmin ? true : false;
            } else {
              userInfo.subscription = subscriptionTier || true;
            }
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
          } catch (e) {
            console.error('Failed to update userInfo in interceptor', e);
          }
        }

        if (hasSubscription === false && !isSuperAdmin) {
          const currentPath = window.location.pathname;
          const isExcluded =
            currentPath.startsWith('/admin/subscription') ||
            currentPath.startsWith('/login') ||
            currentPath.startsWith('/logout') ||
            currentPath.startsWith('/auth');

          if (!isExcluded) {
            globalNavigate('/admin/subscription');
          }
        }
      }
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error || '';
      const msgStr = serverMessage.toUpperCase();

      if (
        status === 401 ||
        status === 403 ||
        msgStr.includes('FORCE_LOGOUT') ||
        msgStr.includes('ACCOUNT_DEACTIVATED') ||
        msgStr.includes('ACCOUNT_DELETED') ||
        msgStr === 'INACTIVE' ||
        msgStr === 'DEACTIVATED' ||
        (msgStr.includes('ACCOUNT') && (msgStr.includes('INACTIVE') || msgStr.includes('DEACTIVATED'))) ||
        (msgStr.includes('USER') && (msgStr.includes('INACTIVE') || msgStr.includes('DEACTIVATED'))) ||
        msgStr.includes('PERMISSIONS HAVE BEEN UPDATED') ||
        msgStr.includes('SYNC YOUR NEW PERMISSIONS')
      ) {
        // Session expired or unauthorized or account inactive — clear credentials and redirect to login
        let displayMsg = 'Your session has expired. Please sign in again.';

        if (msgStr.includes('FORCE_LOGOUT')) {
          displayMsg = 'You have been signed out by an administrator. Please sign in again.';
        } else if (
          msgStr.includes('ACCOUNT_DEACTIVATED') ||
          msgStr === 'INACTIVE' ||
          msgStr === 'DEACTIVATED' ||
          (msgStr.includes('ACCOUNT') && (msgStr.includes('INACTIVE') || msgStr.includes('DEACTIVATED'))) ||
          (msgStr.includes('USER') && (msgStr.includes('INACTIVE') || msgStr.includes('DEACTIVATED')))
        ) {
          displayMsg = 'Your account is inactive. Please contact your administrator.';
        } else if (msgStr.includes('ACCOUNT_DELETED')) {
          displayMsg = 'Your account is no longer active. Please contact your administrator.';
        } else if (msgStr.includes('PASSWORD_CHANGED')) {
          displayMsg = 'Your password was changed. Please sign in again.';
        } else if (msgStr.includes('PERMISSIONS HAVE BEEN UPDATED') || msgStr.includes('SYNC YOUR NEW PERMISSIONS')) {
          displayMsg =
            serverMessage || 'Your permissions have been updated by your administrator. Please log in again to sync your new permissions.';
        }

        triggerForceLogout(displayMsg);
      } else {
        // Show user-friendly error message for all other errors
        const displayMessage = serverMessage || 'Something went wrong. Please try again.';
        console.error('API Error:', status, displayMessage);
        if (!error.config?.skipNotification) {
          showGlobalNotification(displayMessage, 'error');
        }
      }
    } else {
      // Network error or request was cancelled
      if (!error.config?.skipNotification) {
        showGlobalNotification('Something went wrong. Please try again.', 'error');
      }
    }

    return Promise.reject({
      message: error.response?.data?.message || error.message,
      status: error.response?.status,
      data: error.response?.data
    });
  }
);

export default instance;
